import axios from '@/plugins/axios'

/**
 * Serviço centralizado para gerenciar alertas do sistema
 * Verifica periodicamente se há alertas a serem exibidos
 */
class AlertService {
  constructor() {
    this.interval = null
    this.checkInterval = 600000 // Verificar a cada 10 minutos
    this.isChecking = false
  }

  /**
   * Inicia o monitoramento de alertas
   * @param {Function} callback - Função chamada quando um alerta é detectado
   */
  startMonitoring(callback) {
    if (this.interval) {
      console.warn('Monitoramento de alertas já está ativo')

      return
    }

    // Verifica imediatamente ao iniciar
    this.checkAlerts(callback)

    // Depois verifica no intervalo configurado
    this.interval = setInterval(() => {
      this.checkAlerts(callback)
    }, this.checkInterval)

    console.log('Monitoramento de alertas iniciado')
  }

  /**
   * Para o monitoramento de alertas
   */
  stopMonitoring() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
      console.log('Monitoramento de alertas parado')
    }
  }

  /**
   * Verifica todos os alertas disponíveis
   * @param {Function} callback - Função chamada quando um alerta é detectado
   */
  async checkAlerts(callback) {
    // Evita múltiplas verificações simultâneas
    if (this.isChecking) {
      return
    }

    this.isChecking = true

    try {
      // Verifica alerta de auditoria
      await this.checkAuditAlert(callback)

      // Verifica alerta de diárias
      await this.checkDailyAlert(callback)

      // Verifica e executa auditoria automática
      await this.checkAutomaticAudit(callback)

      // Adicione outros alertas aqui conforme necessário
    } catch (error) {
      console.error('Erro ao verificar alertas:', error)
    } finally {
      this.isChecking = false
    }
  }

  /**
   * Verifica se há alerta de auditoria pendente
   * @param {Function} callback
   */
  async checkAuditAlert(callback) {
    try {
      const response = await axios.post('/alerts/audit_alert')

      if (response.data.success === true) {
        callback({
          type: 'audit',
          title: 'Auditoria Pendente',
          message: 'A auditoria do dia ainda não foi executada!',
          icon: 'warning',
          action: '/audit',
          actionText: 'Ir para Auditoria',
        })
      }
    } catch (error) {
      console.error('Erro ao verificar alerta de auditoria:', error)
    }
  }

  /**
   * Verifica se há alerta de diárias pendente
   * @param {Function} callback
   */
  async checkDailyAlert(callback) {
    try {
      const response = await axios.post('/alerts/daily_alert')

      if (response.data.success === true) {
        callback({
          type: 'daily',
          title: 'Diárias Pendentes',
          message: 'A execução de diárias do dia ainda não foi realizada!',
          icon: 'warning',
          action: '/diary_execution',
          actionText: 'Ir para Diárias',
        })
      }
    } catch (error) {
      console.error('Erro ao verificar alerta de diárias:', error)
    }
  }

  /**
   * Verifica e executa auditoria automática se necessário
   * @param {Function} callback
   */
  async checkAutomaticAudit(callback) {
    try {
      const response = await axios.post('/audit/automatic')

      // Se executou com sucesso, exibe notificação
      if (response.data.success === true) {
        callback({
          type: 'automatic_audit',
          title: 'Auditoria Automática Executada',
          message: 'A auditoria automática foi executada com sucesso!',
          icon: 'success',
          action: '/audit',
          actionText: 'Ver Auditoria',
        })
      }

      // Se não executou (false), não faz nada (é normal)
    } catch (error) {
      // Só loga erros 500, não erros 200 com success: false
      if (error.response && error.response.status >= 500) {
        console.error('Erro ao verificar auditoria automática:', error)
      }
    }
  }

  /**
   * Altera o intervalo de verificação
   * @param {number} milliseconds - Novo intervalo em milissegundos
   */
  setCheckInterval(milliseconds) {
    this.checkInterval = milliseconds

    // Se já está monitorando, reinicia com novo intervalo
    if (this.interval) {
      const wasMonitoring = true
      this.stopMonitoring()

      if (wasMonitoring) {
        // Nota: precisa passar o callback novamente ao reiniciar
        console.warn('Intervalo alterado. Reinicie o monitoramento manualmente.')
      }
    }
  }
}

export default new AlertService()
